import { EntityStore } from '@adornis/baseql/client/entityStore.js';
import { type NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import '@adornis/bh-matching-signup/components/matching-comp-signup-closed.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import '@adornis/buchungsheld-components/bh-breadcrumb.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { Product } from '@adornis/buchungsheld-db/entities/products.js';
import '@adornis/buchungsheld-theme/components/tr-button-small.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import '@adornis/buchungsheld-theme/components/tr-tooltip.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { filter, map, switchMap } from 'rxjs';
import { BHPromptTimeframeModify } from './bh-prompt-timeframe-modify.js';
import { BHPromptTimeframeSync } from './bh-prompt-timeframe-sync.js';

@customElement('bh-page-admin-einrichtung-timeframes')
export class BHPageAdminEinrichtungTimeframes extends BuchungsheldLitElement {
  @property({ attribute: 'institution-id' }) institutionID = new RXController<string | null>(this, null);

  @state() private readonly _timeframes = new RXController(
    this,
    this.institutionID.observable.pipe(
      filter(Boolean),
      switchMap(id => Timeframe.subscribeTimeframesByInstitution(id)(Timeframe.allFields)),
      map(timeframes =>
        timeframes.sort((a: Timeframe, b: Timeframe) => {
          return a.from < b.from ? 1 : -1;
        }),
      ),
    ),
  );

  override render() {
    if (
      this._timeframes.isLoading ||
      !this._timeframes.value ||
      this.institutionID.isLoading ||
      !this.institutionID.value
    )
      return html` <tr-loading-state></tr-loading-state> `;

    return html` <tr-flex space="md"> ${this._breadcrumbs()} ${this._content()} </tr-flex> `;
  }

  protected _content() {
    return html`
      ${this._timeframes.value?.length === 0
        ? html`
            <tr-section>
              <matching-comp-signup-closed></matching-comp-signup-closed>
            </tr-section>
          `
        : nothing}
      ${this._timeframes.value?.map(timeframe => {
        return this._renderTimeframe(timeframe);
      }) ?? nothing}
    `;
  }

  protected _breadcrumbs() {
    return html`
      <bh-breadcrumb
        .items=${[{ content: this._translation.translate('SIDEBAR', 'timeframes'), active: true }]}
        .additionalAction=${html`
          <tr-button-small icon="add" .action=${() => BHPromptTimeframeModify.add(this.institutionID.value!)}>
            <tr-text>${this._translation.translate('timeframe', 'add')}</tr-text>
            <tr-tooltip .text=${this._translation.translate('timeframe', 'add')}> </tr-tooltip>
          </tr-button-small>
        `}
      ></bh-breadcrumb>
    `;
  }

  protected _renderTimeFrameContent(timeframe: Timeframe) {
    return html`
      <tr-h3>${timeframe.name}</tr-h3>
      <tr-text>${this._translation.translate('from')}: ${timeframe.from.toLocaleString(DateTime.DATE_MED)}</tr-text>
      <tr-text>${this._translation.translate('till')}: ${timeframe.till.toLocaleString(DateTime.DATE_MED)}</tr-text>
      ${timeframe.publishDate
        ? html`<tr-text>
            ${this._translation.translate('field', 'publishDate')}:
            ${timeframe.publishDate.toLocaleString(DateTime.DATE_MED)}</tr-text
          >`
        : nothing}

      <tr-hr></tr-hr>

      <tr-flex horizontal space="sm" space-between>
        <tr-flex space="sm" horizontal flex>
          ${this._actionActiveRender(timeframe)} ${this._actionAllowRegistration(timeframe)}
        </tr-flex>
        ${this._timeframeActions(timeframe)}
      </tr-flex>
    `;
  }

  protected _renderTimeframe(timeframe: Timeframe) {
    return html`
      <tr-card ${css({ width: '100%' })}>
        <tr-flex space="sm"> ${this._renderTimeFrameContent(timeframe)} </tr-flex>
      </tr-card>
    `;
  }

  protected _renderTimeframeActionsAccent(timeframe: Timeframe) {
    return html`
      <tr-button-small icon="edit" mode="outline" .action=${() => BHPromptTimeframeModify.edit(timeframe)}>
        <tr-tooltip .text=${this._translation.translate('edit')} placement="bottom"></tr-tooltip>
      </tr-button-small>
      <tr-button-small
        icon="directory_sync"
        mode="outline"
        .action=${() => BHPromptTimeframeSync.sync(this.institutionID.value, timeframe)}
      >
        <tr-tooltip
          .text=${html`
            ${this._translation.translate('timeframe', 'sync_from')} ${this._translation.translate('timeframe')}
          `}
          placement="bottom"
        ></tr-tooltip>
      </tr-button-small>
    `;
  }

  protected _renderTimeframeActionsRedColor(timeframe: Timeframe) {
    return html`
      <tr-button-small
        icon="delete"
        mode="outline"
        .color=${this.colors.tone.error}
        .action=${async () => {
          if (await TrDialog.confirm(this._translation.translate('timeframe', 'confirm_deletion'))) {
            await timeframe.remove();
            // FIXME: this should propably not be neceassary, but whithout it workshops in BHPageAdminEinrichtungWorkshopsModify do not show new Timeframe values
            EntityStore.drop(Product as any);
          }
        }}
      >
        <tr-tooltip .text=${this._translation.translate('delete')} placement="bottom"></tr-tooltip>
      </tr-button-small>
    `;
  }

  protected _timeframeActions(timeframe: Timeframe) {
    return html`
      <tr-flex space="sm" horizontal>
        ${[this._renderTimeframeActionsAccent(timeframe), this._renderTimeframeActionsRedColor(timeframe)]}
      </tr-flex>
    `;
  }

  protected _actionAllowRegistration(timeframe: Timeframe) {
    const isTimeRunOut = timeframe.till < DateTime.now();
    const isInTimeframe = timeframe.from <= DateTime.now() && timeframe.till > DateTime.now();

    if (isTimeRunOut || isInTimeframe) return nothing;

    if (timeframe.isRegistrationPossibleBesidesTimeframe) {
      return html`
        <tr-button-small
          .color=${this.colors.tone.lightGrey}
          .action=${async () => {
            const isConfirmed = await TrDialog.confirm(html`
              <tr-flex space="sm" ${css({ paddingBottom: this.spacing.md })}>
                <tr-h3 bold>${this._translation.translate('timeframe', 'deactivate_registration')}</tr-h3>
                <tr-text>${this._translation.translate('timeframe', 'deactivate_registration_wrn')}</tr-text>
              </tr-flex>
            `);
            if (!isConfirmed) return;

            timeframe.isRegistrationPossibleBesidesTimeframe = false;
            await timeframe.save();
            this.requestUpdate();
          }}
        >
          <tr-text bold>${this._translation.translate('timeframe', 'registration_open')}</tr-text>
        </tr-button-small>
      `;
    }
    return html`
      <tr-button-small
        mode="outline"
        .color=${this.colors.tone.lightGrey}
        .action=${async () => {
          const isConfirmed = await TrDialog.confirm(html`
            <tr-flex space="sm" ${css({ paddingBottom: this.spacing.md })}>
              <tr-h3 bold>${this._translation.translate('timeframe', 'activate_registration')}</tr-h3>
              <tr-text>${this._translation.translate('timeframe', 'activate_registration_wrn')}</tr-text>
            </tr-flex>
          `);
          if (!isConfirmed) return;

          timeframe.isRegistrationPossibleBesidesTimeframe = true;
          await timeframe.save();
          this.requestUpdate();
        }}
      >
        <tr-text bold>${this._translation.translate('timeframe', 'registration_soon_available')}</tr-text>
      </tr-button-small>
    `;
  }

  protected _actionActiveRender(timeframe: Timeframe) {
    const isTimeRunOut = timeframe.till < DateTime.now();

    if (isTimeRunOut) return this._actionStateTimeRunOut();
    if (timeframe.isActive) {
      return html`
        <tr-button-small
          icon=${'wifi'}
          .color=${this.colors.tone.success}
          .action=${async () => {
            await this.deactivateTimeframe(timeframe);
          }}
        >
          <tr-text bold>Online</tr-text>
        </tr-button-small>
      `;
    }
    return html`
      <tr-button-small
        icon=${'warning'}
        .color=${this.colors.tone.warning}
        .action=${async () => {
          await this.activateTimeframe(timeframe);
        }}
      >
        <tr-text bold>Offline</tr-text>
      </tr-button-small>
    `;
  }

  protected async deactivateTimeframe(timeframe: NarrowedEntity<Timeframe, { _id: 1; isActive: 1 }>, silent?: boolean) {
    if (!silent) {
      const isConfirmed = await TrDialog.confirm(html`
        <tr-flex space="sm" ${css({ paddingBottom: this.spacing.md })}>
          <tr-h3 bold>
            ${this._translation.translate('field', 'timeframe')} ${this._translation.translate('deactivate')}?
          </tr-h3>
          <tr-text>${this._translation.translate('timeframe', 'deactivate_registration_wrn')}</tr-text>
        </tr-flex>
      `);
      if (!isConfirmed) return;
    }

    timeframe.isActive = false;
    await timeframe.save();
    this.requestUpdate();
  }

  protected async activateTimeframe(timeframe: NarrowedEntity<Timeframe, { _id: 1; isActive: 1 }>, silent?: boolean) {
    if (!silent) {
      const isConfirmed = await TrDialog.confirm(html`
        <tr-flex space="sm" ${css({ paddingBottom: this.spacing.md })}>
          <tr-h3 bold>${this._translation.translate('timeframe')} ${this._translation.translate('activate')}?</tr-h3>
          <tr-text>${this._translation.translate('timeframe', 'activate_registration_wrn')}</tr-text>
        </tr-flex>
      `);
      if (!isConfirmed) return;
    }

    timeframe.isActive = true;
    await timeframe.save();
    this.requestUpdate();
  }

  private _actionStateTimeRunOut() {
    return html`
      <tr-button-small icon=${'wifi_off'} .color=${this.colors.tone.lightGrey} not-clickable mode="outline">
        <tr-text bold
          >${this._translation.translate('timeframe')} ${this._translation.translate('timeframe', 'over')}</tr-text
        >
      </tr-button-small>
    `;
  }
}
